import React, { useEffect, useState, useMemo } from 'react';
import { useAppContext } from '../../context/AppContext';
import { ChevronRight, ChevronDown, FileText, Calendar, Hash } from 'lucide-react';

interface HierarchicalNode {
  type: string;
  node_type: string;
  id: string;
  label: string;
  group?: string;
  level: number;
  metadata: any;
  drawing_title?: string;
  drawing_number?: string;
  revision?: string;
  revision_date?: string;
}

interface ComponentMetadata {
  id: string;
  originalId: string;
  label: string;
  nodeType: string;
  level: number;
  group?: string;
  metadata: Record<string, any>;
  revision: string;
  revisionDate?: string;
  drawingInfo?: {
    title: string;
    number: string;
  };
}

const ComponentMetadataTab: React.FC = () => {
  const { state, entities, loading: contextLoading, error: contextError, setSelectedComponents } = useAppContext();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Convert entities from context to hierarchical format
  const hierarchyData = useMemo(() => {
    if (!entities.length) return null;

    const nodes: HierarchicalNode[] = entities.map(entity => {
      const entityName = entity.entity_name || entity.name;
      const entityId = entity.entity_id || entity.id;
      const componentType = entity.pvcollada_component_type || entity.entity_type_name || entity.concept_category || 'Unknown';
      const nodeType = componentType.toLowerCase();
      const entityMetadata = entity.entity_metadata || entity.metadata || entity.properties || {};

      return {
        type: componentType,
        node_type: nodeType,
        id: entityName,
        label: entityName,
        level: nodeType.includes('module') ? 3 : nodeType.includes('combiner') ? 2 : nodeType.includes('inverter') ? 1 : 0,
        metadata: {
          ...entityMetadata,
          entity_id: entityId,
          asset_tag: entity.asset_tag || entity.serial_number,
          installation_date: entity.installation_date,
          status: entity.status,
        },
        drawing_title: entityMetadata.drawing_title,
        drawing_number: entityMetadata.drawing_number,
        revision: entityMetadata.revision || '1',
        revision_date: entityMetadata.revision_date,
      };
    });

    return { nodes };
  }, [entities]);

  // Create alias for component ID
  const createAlias = (id: string, nodeType: string): string => {
    if (nodeType === 'inverter' && /^INVERTER_/.test(id)) {
      // Extract numeric part and drop the .1 suffix: "INVERTER_2.8.1" -> "2.8"
      const match = id.match(/INVERTER_(.+)/);
      if (match) {
        const parts = match[1].split('.');
        if (parts.length >= 2) {
          return `${parts[0]}.${parts[1]}`;  // Take first two parts: "2.8"
        }
        return match[1];
      }
      return id;
    }
    return id;
  };

  // Filter and process selected components
  const selectedComponentsData = useMemo(() => {
    if (!hierarchyData || state.selectedComponents.length === 0) return [];

    const nodeMap = new Map<string, HierarchicalNode>();
    const aliasMap = new Map<string, string>();

    // Build maps
    hierarchyData.nodes.forEach(node => {
      const alias = createAlias(node.id, node.node_type);
      nodeMap.set(node.id, node);
      aliasMap.set(alias, node.id);
    });

    const components: ComponentMetadata[] = [];

    state.selectedComponents.forEach(selectedId => {
      // Try direct match first, then alias match
      let node = nodeMap.get(selectedId);
      if (!node && aliasMap.has(selectedId)) {
        node = nodeMap.get(aliasMap.get(selectedId)!);
      }

      if (node) {
        // Filter non-null metadata
        const filteredMetadata: Record<string, any> = {};
        Object.entries(node.metadata || {}).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            filteredMetadata[key] = value;
          }
        });

        components.push({
          id: selectedId,
          originalId: node.id,
          label: node.label,
          nodeType: node.node_type,
          level: node.level,
          group: node.group,
          metadata: filteredMetadata,
          revision: node.revision || '1',
          revisionDate: node.revision_date,
          drawingInfo: node.drawing_title && node.drawing_number ? {
            title: node.drawing_title,
            number: node.drawing_number
          } : undefined
        });
      }
    });

    // Sort by level (hierarchy) then by label
    return components.sort((a, b) => {
      if (a.level !== b.level) return a.level - b.level;
      return a.label.localeCompare(b.label);
    });
  }, [hierarchyData, state.selectedComponents]);

  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  const renderMetadataValue = (key: string, value: any) => {
    if (typeof value === 'object' && value !== null) {
      return (
        <div className="ml-4">
          {Object.entries(value).map(([subKey, subValue]) => (
            <div key={subKey} className="py-1">
              <span className="text-engramiq-bright-grey text-xs">{subKey}:</span>
              <span className="ml-2 text-engramiq-light-grey text-xs">{String(subValue)}</span>
            </div>
          ))}
        </div>
      );
    }

    // Format specific values
    if (typeof value === 'number') {
      return <span className="text-engramiq-green font-mono text-xs">{value.toLocaleString()}</span>;
    }

    if (typeof value === 'boolean') {
      return <span className={`text-xs font-medium ${value ? 'text-engramiq-green' : 'text-accent-red'}`}>
        {value ? 'Yes' : 'No'}
      </span>;
    }

    return <span className="text-engramiq-light-grey text-xs">{String(value)}</span>;
  };

  if (contextLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-engramiq-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-engramiq-light-grey font-medium">Loading...</div>
        </div>
      </div>
    );
  }

  if (contextError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
        <div className="text-center p-8">
          <div className="text-accent-red text-xl mb-2">⚠️</div>
          <div className="text-engramiq-light-grey font-medium mb-2">Failed to load component data</div>
          <div className="text-engramiq-bright-grey text-sm">{contextError}</div>
        </div>
      </div>
    );
  }

  if (selectedComponentsData.length === 0) {
    // Show list of all components when none selected
    const allComponents = hierarchyData?.nodes || [];

    if (allComponents.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-engramiq-dark-blue">
          <div className="text-center p-8">
            <div className="text-engramiq-bright-grey text-lg mb-2">📋</div>
            <div className="text-engramiq-light-grey font-medium">No Components Available</div>
            <div className="text-engramiq-bright-grey text-sm mt-2">
              No components found for the selected site/as-built
            </div>
          </div>
        </div>
      );
    }

    // Group components by type
    const componentsByType = allComponents.reduce((acc, node) => {
      const type = node.type || 'Unknown';
      if (!acc[type]) acc[type] = [];
      acc[type].push(node);
      return acc;
    }, {} as Record<string, HierarchicalNode[]>);

    return (
      <div className="w-full h-full bg-engramiq-dark-blue overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0 p-4 border-b border-engramiq-green/20 bg-surface-800/50">
          <h3 className="text-lg font-bold text-engramiq-light-grey mb-1">All Components</h3>
          <div className="text-xs text-engramiq-bright-grey">
            {allComponents.length} component{allComponents.length !== 1 ? 's' : ''} • Click to select
          </div>
        </div>

        {/* List View */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {Object.entries(componentsByType).sort(([a], [b]) => a.localeCompare(b)).map(([type, components]) => (
            <div key={type}>
              <h4 className="text-sm font-semibold text-engramiq-green mb-3 flex items-center gap-2">
                <span className="bg-engramiq-green/20 px-2 py-1 rounded">{type}</span>
                <span className="text-engramiq-bright-grey font-normal">({components.length})</span>
              </h4>
              <div className="space-y-2">
                {components.sort((a, b) => a.label.localeCompare(b.label)).map((component) => (
                  <button
                    key={component.id}
                    onClick={() => {
                      // Select this component
                      const alias = createAlias(component.id, component.node_type);
                      setSelectedComponents([alias]);
                    }}
                    className="w-full text-left p-3 bg-surface-800/30 hover:bg-surface-800/50 border border-engramiq-green/10 hover:border-engramiq-green/30 rounded transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-medium text-engramiq-light-grey">
                          {component.label}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-engramiq-bright-grey">
                          <span>Level {component.level}</span>
                          {component.group && <span>• {component.group}</span>}
                          {component.revision && <span>• Rev {component.revision}</span>}
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-engramiq-green/50 flex-shrink-0" />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-engramiq-dark-blue overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-engramiq-green/20 bg-surface-800/50">
        <h3 className="text-lg font-bold text-engramiq-light-grey mb-1">Component Metadata</h3>
        <div className="flex items-center gap-4 text-xs text-engramiq-bright-grey">
          <span>{selectedComponentsData.length} component{selectedComponentsData.length !== 1 ? 's' : ''} selected</span>
          <span>Hierarchy levels {Math.min(...selectedComponentsData.map(c => c.level))} - {Math.max(...selectedComponentsData.map(c => c.level))}</span>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {selectedComponentsData.map((component) => {
          const sectionId = `component-${component.id}`;
          const isExpanded = expandedSections.has(sectionId);

          return (
            <div key={component.id} className="bg-surface-800/30 rounded-lg border border-engramiq-green/20">
              {/* Component Header */}
              <button
                onClick={() => toggleSection(sectionId)}
                className="w-full p-4 text-left hover:bg-surface-800/50 transition-colors rounded-t-lg"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-engramiq-green flex-shrink-0" />
                    )}
                    <div>
                      <div className="text-sm font-semibold text-engramiq-light-grey">
                        {component.label}
                      </div>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-xs text-engramiq-bright-grey">
                          Level {component.level} • {component.nodeType.replace('_', ' ')}
                        </span>
                        {component.group && (
                          <span className="text-xs text-engramiq-green bg-engramiq-green/20 px-2 py-0.5 rounded">
                            {component.group}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-xs text-engramiq-bright-grey">
                      <Hash className="w-3 h-3" />
                      Rev {component.revision}
                    </div>
                    {component.revisionDate && (
                      <div className="flex items-center gap-1 text-xs text-engramiq-bright-grey">
                        <Calendar className="w-3 h-3" />
                        {component.revisionDate}
                      </div>
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="px-4 pb-4">
                  <div className="border-t border-engramiq-green/10 pt-4 space-y-4">
                    
                    {/* Drawing Information */}
                    {component.drawingInfo && (
                      <div>
                        <h4 className="text-sm font-semibold text-engramiq-green mb-2 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Drawing Information
                        </h4>
                        <div className="bg-surface-800/50 rounded p-3 space-y-1">
                          <div className="flex justify-between">
                            <span className="text-xs text-engramiq-bright-grey">Title:</span>
                            <span className="text-xs text-engramiq-light-grey">{component.drawingInfo.title}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-xs text-engramiq-bright-grey">Number:</span>
                            <span className="text-xs text-engramiq-light-grey font-mono">{component.drawingInfo.number}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Component Metadata */}
                    {Object.keys(component.metadata).length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-engramiq-green mb-2">
                          Technical Specifications
                        </h4>
                        <div className="bg-surface-800/50 rounded p-3 space-y-2">
                          {Object.entries(component.metadata).map(([key, value]) => (
                            <div key={key} className="flex justify-between items-start">
                              <span className="text-xs text-engramiq-bright-grey capitalize mr-2 flex-shrink-0">
                                {key.replace(/_/g, ' ')}:
                              </span>
                              <div className="text-right flex-1">
                                {renderMetadataValue(key, value)}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Component IDs */}
                    <div>
                      <h4 className="text-sm font-semibold text-engramiq-green mb-2">
                        Component Identifiers
                      </h4>
                      <div className="bg-surface-800/50 rounded p-3 space-y-1">
                        <div className="flex justify-between">
                          <span className="text-xs text-engramiq-bright-grey">Alias ID:</span>
                          <span className="text-xs text-engramiq-light-grey font-mono">{component.id}</span>
                        </div>
                        {component.originalId !== component.id && (
                          <div className="flex justify-between">
                            <span className="text-xs text-engramiq-bright-grey">Original ID:</span>
                            <span className="text-xs text-engramiq-light-grey font-mono">{component.originalId}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ComponentMetadataTab;